import * as React from 'react';
import { Button, TextField, Link, Typography, InputAdornment } from '@mui/material';
import { Box } from '@mui/system';
import CircularProgress from '@mui/material/CircularProgress';
import { useQueryClient } from '@tanstack/react-query';
import { AppContext } from '../../../AppContext';


interface InvokeAgentBoxProps {
  agentId: string;
  chatsCacheName: string;
  currentChatId?: string;
  onMessageSent: (chatId: string, input: string) => void;
}

// Bottom input bar: continues the active chat when one is selected, otherwise starts a new
// chat. Refreshes the chats list (keyed by chatsCacheName) after sending.
export default function InvokeAgentBox({agentId, chatsCacheName, currentChatId, onMessageSent}: InvokeAgentBoxProps) {
  const {client, notificationsService} = React.useContext(AppContext);
  const queryClient = useQueryClient();
  const [input, setInput] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [isMultiline, setIsMultiline] = React.useState(false);
  const inputRef = React.useRef<HTMLTextAreaElement>(null);

  function syncMultiline() {
    const el = inputRef.current;
    if (el) {
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
      setIsMultiline(el.value.includes('\n') || el.scrollHeight > lineHeight * 1.5);
    }
  }

  async function send() {
    if (!input || loading) {
      return;
    }

    const sentInput = input;
    setLoading(true);
    const result = await client<any, any>({
      event: 'aiagents.sendMessage',
      data: currentChatId ? {chatId: currentChatId, input: sentInput} : {id: agentId, input: sentInput},
    });
    setLoading(false);

    if (result.success) {
      setInput('');
      setIsMultiline(false);
      onMessageSent(result.value, sentInput);
      await queryClient.invalidateQueries([chatsCacheName]);
    } else {
      notificationsService.notify({
        severity: 'error',
        message: `Error: ${JSON.stringify(result.errors)}`
      });
    }
  }

  function onKeyDown(event: React.KeyboardEvent) {
    // Enter sends; Shift+Enter inserts a newline.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void send();
    }
  }

  return (
    <Box sx={{px: 2, pt: 1.5, pb: 0.75, borderTop: (theme) => `1px solid ${theme.palette.divider}`}}>
      <TextField
        id="input"
        name="input"
        label="input"
        variant="outlined"
        fullWidth
        multiline
        maxRows={6}
        inputRef={inputRef}
        value={input}
        onChange={(event) => {
          setInput(event.target.value);
          syncMultiline();
        }}
        onKeyDown={onKeyDown}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{alignSelf: isMultiline ? 'flex-end' : 'center', mb: isMultiline ? 1 : 0}}>
              {
                loading
                ? <CircularProgress size={32} sx={{ m: 0.5 }}/>
                : <Button variant="contained" onClick={send} disabled={!input}>Send</Button>
              }
            </InputAdornment>
          ),
        }}
      />
      <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 0.5, textAlign: 'center'}}>
        👋 New to working with AI agents? Clear, well-structured prompts get better results. Learn how to{' '}
        <Link href="https://cloud.google.com/discover/what-is-prompt-engineering" target="_blank" rel="noopener noreferrer">
          write effective prompts
        </Link>
        {' '}and{' '}
        <Link
          href="https://docs.cloud.google.com/gemini-enterprise-agent-platform/models/prompts/introduction-prompt-design"
          target="_blank"
          rel="noopener noreferrer"
        >
          design prompts for agents
        </Link>
        .
      </Typography>
    </Box>
  );
}
